import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImgIdx, setModalImgIdx] = useState(0);
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`https://kaushal.calisearch.org/fetch_property.php?id=${id}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch property details");
        return res.json();
      })
      .then(data => {
        setProperty(data);
        setLoading(false);
        setImgIdx(0);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;
  if (!property) return <div>No property found.</div>;

  const images = property.images || [];
  const mainImage = images[0];
  const gridImages = images.slice(1, 5);
  const extraCount = images.length - 5;

  const showPrev = () => setImgIdx((i) => (i === 0 ? images.length - 1 : i - 1));
  const showNext = () => setImgIdx((i) => (i === images.length - 1 ? 0 : i + 1));

  // Modal for image details
  const handleImageClick = () => {
    setModalImgIdx(imgIdx);
    setModalOpen(true);
  };
  const handleModalClose = (e) => {
    if (e.target.className === 'modal-overlay' || e.target.className === 'modal-close') {
      setModalOpen(false);
    }
  };
  const modalShowPrev = (e) => {
    e.stopPropagation();
    setModalImgIdx((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const modalShowNext = (e) => {
    e.stopPropagation();
    setModalImgIdx((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  // Address and subtitle
  const address = property.address || property.L_Address || "Property";
  const subtitle = [property.City, property.State, property.ZIP].filter(Boolean).join(", ");
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyB0szWlIt9Vj26cM300wTcWxwL0ABHZ9HE";

  return (
    <div className="property-details-container">
      <div className="property-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h1 className="property-title" style={{ marginBottom: 0 }}>{address}</h1>
          {subtitle && <div className="property-subtitle">{subtitle}</div>}
        </div>
        {property.lat && property.lng && (
          <button
            style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontWeight: 600, fontSize: '1.08rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(25,118,210,0.10)' }}
            onClick={() => setShowMap(v => !v)}
          >
            {showMap ? 'Hide Map' : 'Map View'}
          </button>
        )}
      </div>
      {/* Gallery and Info Content - left aligned */}
      <div className="property-content">
        <div className="property-gallery-row">
          <div className="main-image-col">
            {mainImage && (
              <img
                src={mainImage}
                alt="Main Property"
                className="main-gallery-image"
                style={{ width: '100%', height: 340, objectFit: 'cover', borderRadius: 14, cursor: 'zoom-in', background: '#f3f6fa' }}
                onClick={() => { setModalImgIdx(0); setModalOpen(true); }}
              />
            )}
          </div>
          <div className="side-images-col">
            {gridImages.map((img, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <img
                  src={img}
                  alt={`Gallery ${idx + 2}`}
                  className="side-gallery-image"
                  style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, cursor: 'zoom-in', background: '#f3f6fa' }}
                  onClick={() => { setModalImgIdx(idx + 1); setModalOpen(true); }}
                />
                {idx === 3 && extraCount > 0 && (
                  <button
                    className="photo-count-btn"
                    style={{ position: 'absolute', bottom: 12, right: 12, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
                    onClick={() => { setModalImgIdx(4); setModalOpen(true); }}
                  >
                    {images.length} photos
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Property Info Card below gallery, left-aligned */}
        <div className="property-info">
          <p><b>Price:</b> ${Number(property.price).toLocaleString()}</p>
          <p><b>Beds:</b> {property.beds}</p>
          <p><b>Baths:</b> {property.baths}</p>
          <p><b>Sq Ft:</b> {property.sqft}</p>
          <div className="property-actions">
            <button className="action-btn">Schedule Tour</button>
            <button className="action-btn">Contact Agent</button>
          </div>
        </div>
      </div>
      {property.about && (
        <div className="about-home-section" style={{ marginTop: 36, background: '#f7fafc', borderRadius: 12, padding: '28px 28px 18px 28px', boxShadow: '0 1.5px 8px rgba(30,42,80,0.04)' }}>
          <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#1976d2', marginBottom: 12 }}>About this Home</h2>
          <div style={{ color: '#2d3748', fontSize: '1.08rem', whiteSpace: 'pre-line' }}>
            {showFullAbout || property.about.length <= 300
              ? property.about
              : property.about.slice(0, 300) + '...'}
          </div>
          {property.about.length > 300 && (
            <button
              style={{ marginTop: 10, background: 'none', border: 'none', color: '#1976d2', fontWeight: 600, cursor: 'pointer', fontSize: '1.05rem' }}
              onClick={() => setShowFullAbout(v => !v)}
            >
              {showFullAbout ? 'Show Less' : 'Show More'}
            </button>
          )}
        </div>
      )}
      {/* Property Map View */}
      {showMap && property.lat && property.lng && (
        <div className="property-map-section" style={{ margin: '0 0 36px 0', background: '#f7fafc', borderRadius: 12, padding: 0, boxShadow: '0 1.5px 8px rgba(30,42,80,0.04)', width: '100%' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1976d2', margin: '0 0 12px 24px', paddingTop: 24 }}>Location</h2>
          <div style={{ width: '100%', height: 350, border: '2px solid #1976d2', borderRadius: 12, overflow: 'hidden', marginBottom: 18, background: '#e3eaf6' }}>
            <PropertyMap lat={Number(property.lat)} lng={Number(property.lng)} apiKey={apiKey} />
          </div>
        </div>
      )}
      {/* Modal for image details */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content">
            <button className="modal-close" onClick={handleModalClose}>&times;</button>
            <div className="modal-carousel-wrapper">
              <button className="carousel-btn left" onClick={modalShowPrev}>&lt;</button>
              <img
                src={images[modalImgIdx]}
                alt={`Property ${modalImgIdx + 1}`}
                className="modal-image-large"
              />
              <button className="carousel-btn right" onClick={modalShowNext}>&gt;</button>
            </div>
            <div className="modal-caption">
              Image {modalImgIdx + 1} of {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;

function PropertyMap({ lat, lng, apiKey }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey });
  if (!isLoaded) return <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading Map...</div>;
  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={{ lat, lng }}
      zoom={15}
      options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
    >
      <Marker position={{ lat, lng }} />
    </GoogleMap>
  );
} 